import { useQuery } from "@apollo/client"
import { parseISO } from "date-fns"
import React, { useCallback } from "react"
import { Link, useParams } from "react-router-dom"
import { gql } from "~/__generated__"
import { formatRelativeTime } from "~/common/format-relative-time"
import { lettermemeArchivePath } from "~/common/paths"
import { isNonNullable } from "~/common/type-guards"
import { useDocumentTitle } from "~/common/use-document-title"
import { useInfiniteScroll } from "~/common/use-infinite-scroll"
import { useTrackingCode } from "~/common/use-tracking-code"
import { useNav } from "~/root/nav-context"
import { GraphqlError } from "~/ui/errors"
import { ScreenLoading } from "~/ui/screen-loading"
import Text from "~/ui/typography"
import { LettermemeFooter } from "./lettermeme-footer"
import { SubscribeForm } from "./subscribe-form"

const PER_PAGE = 10
const NEWSLETTER_OVERVIEW_MAX_CHARS = 220

const lettermemeHomeQuery = gql(/* GraphQL */ `
  query lettermemeHome($urlPath: String!, $first: Int!, $after: String) {
    lettermeme(urlPath: $urlPath) {
      id
      name
      publicDescription
      urlPath
      viewerIsSubscribed
      viewerIsOwner
      privacy
      trackingCode
      monetizationEnabled
      subscriptionPriceCents
      subscriptionCurrency
      dailyNewsletters(first: $first, after: $after) {
        edges {
          node {
            id
            createdAt
            title
            originalImageUrl
            overview
            shortOverview
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`)

export const LettermemeHomeScreen: React.FC = () => {
  const { urlPath } = useParams<{ urlPath: string }>()
  const { data, error, loading, fetchMore } = useQuery(lettermemeHomeQuery, {
    variables: { urlPath: urlPath!, first: PER_PAGE },
    skip: !urlPath,
  })

  const newsletters =
    data?.lettermeme?.dailyNewsletters.edges.map((edge) => edge.node).filter(isNonNullable) ?? []
  const hasNextPage = data?.lettermeme?.dailyNewsletters.pageInfo.hasNextPage ?? false
  const endCursor = data?.lettermeme?.dailyNewsletters.pageInfo.endCursor

  const handleLoadMore = useCallback(() => {
    if (!endCursor) return
    fetchMore({
      variables: {
        urlPath: urlPath!,
        first: PER_PAGE,
        after: endCursor,
      },
    })
  }, [fetchMore, urlPath, endCursor])

  const observerRef = useInfiniteScroll({
    hasNextPage,
    loading,
    onLoadMore: handleLoadMore,
  })

  useNav({ show: false })
  useDocumentTitle(data?.lettermeme?.name || "Lettermeme")
  useTrackingCode(data?.lettermeme?.trackingCode)

  if (error) return <GraphqlError error={error} />
  if (loading && newsletters.length === 0) return <ScreenLoading />

  if (!data?.lettermeme) {
    return (
      <div className="mx-auto max-w-3xl p-8">
        <Text as="h1" className="mb-4 text-2xl font-bold">
          Lettermeme not found
        </Text>
        <Text>The lettermeme you're looking for doesn't exist.</Text>
      </div>
    )
  }

  const lettermeme = data.lettermeme

  return (
    <div className="p-8">
      <div className="mx-auto flex max-w-3xl flex-col gap-6">
        <div className="mt-28 flex flex-col gap-2 md:mt-44">
          <Text as="h1" className="text-5xl font-normal leading-tight">
            {lettermeme.name}
          </Text>
          {lettermeme.publicDescription && (
            <Text as="div" className="text-lg leading-snug text-gray-400">
              {lettermeme.publicDescription}
            </Text>
          )}
        </div>
        <div className="">
          <SubscribeForm
            lettermemeId={lettermeme.id}
            isSubscribed={lettermeme.viewerIsSubscribed ?? false}
            isPrivate={lettermeme.privacy === "PRIVATE"}
            monetizationEnabled={lettermeme.monetizationEnabled}
            subscriptionPriceCents={lettermeme.subscriptionPriceCents}
            subscriptionCurrency={lettermeme.subscriptionCurrency}
          />
        </div>

        {newsletters.length > 0 ? (
          <div className="mt-3 flex flex-col gap-8">
            {newsletters.map((newsletter) => {
              const overview = newsletter.shortOverview ?? newsletter.overview
              const normalizedOverview = overview?.trim() ?? ""
              const shouldClampOverview =
                normalizedOverview.length > 0 &&
                normalizedOverview.length > NEWSLETTER_OVERVIEW_MAX_CHARS

              return (
                <Link
                  key={newsletter.id}
                  to={lettermemeArchivePath({ urlPath: lettermeme.urlPath, id: newsletter.id })}
                  className="grid grid-cols-1 gap-4 transition-colors hover:bg-gray-900/50 md:grid-cols-[2fr_3fr] md:gap-6"
                >
                  {/* mobile */}
                  <Text as="h3" className="text-lg font-bold md:hidden">
                    {newsletter.title || "Newsletter"}
                  </Text>

                  {newsletter.originalImageUrl && (
                    <div>
                      <img
                        src={newsletter.originalImageUrl}
                        alt={newsletter.title || "Newsletter"}
                        className="h-auto w-full"
                      />
                    </div>
                  )}

                  {/* desktop */}
                  <div className="hidden flex-col justify-start md:flex">
                    <Text as="h3" className="mb-2 text-xl font-bold">
                      {newsletter.title || "Newsletter"}
                    </Text>
                    <Text as="div" className="mb-2 text-sm font-light">
                      {formatRelativeTime(parseISO(newsletter.createdAt))}
                    </Text>
                    <div className="space-y-1 text-sm">
                      {normalizedOverview.length > 0 && (
                        <Text
                          as="p"
                          className={`${shouldClampOverview ? "line-clamp-4" : ""} whitespace-pre-wrap break-words`}
                        >
                          {normalizedOverview}
                        </Text>
                      )}
                      <span className="inline-flex h-auto border-0 p-0 text-xs font-normal text-gray-400 underline-offset-4 hover:underline">
                        more
                      </span>
                    </div>
                  </div>
                </Link>
              )
            })}

            <div ref={observerRef} className="h-1" />
            {loading && <NewsletterSkeleton />}
          </div>
        ) : (
          <div className="rounded-lg bg-gray-50 p-6">
            <Text className="text-gray-600">No newsletters have been published yet.</Text>
          </div>
        )}

        <LettermemeFooter />
      </div>
    </div>
  )
}

const NewsletterSkeleton = () => (
  <div className="grid animate-pulse grid-cols-1 gap-4 md:grid-cols-[2fr_3fr] md:gap-6">
    <div className="aspect-square rounded bg-gray-700" />

    <div className="hidden flex-col justify-center gap-3 md:flex">
      <div className="h-6 w-3/4 rounded bg-gray-700" />
      <div className="h-4 w-1/4 rounded bg-gray-700" />
      <div className="space-y-2">
        <div className="h-3 w-full rounded bg-gray-700" />
        <div className="h-3 w-5/6 rounded bg-gray-700" />
      </div>
    </div>
  </div>
)
